import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';
import { Base64 } from 'js-base64';
import { fromEvent, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { PowerDmsTokensApiModel } from '@app/api/api-models';
import { ConfigService } from '@app/configuration/config.service';
import { TokenStorageService } from '@shared/services';
import { PowerDmsFileInfo } from '@shared/services/power-dms-file-picker.service';
import { CookieService } from 'ngx-cookie-service';

@Component({
  selector: 'a360-power-dms-file-picker',
  templateUrl: './power-dms-file-picker.component.html',
  styleUrls: ['./power-dms-file-picker.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class PowerDmsFilePickerComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input()
  public tokens: PowerDmsTokensApiModel;

  @Input()
  public onSelectionCallback: ((_links: string[]) => void) | ((_files: PowerDmsFileInfo[]) => void);

  @Input()
  public useStructuredCallback = false;

  @Input()
  public onClosedCallback: () => void;

  @ViewChild('backdrop') public backdropRef: ElementRef<HTMLElement>;

  public url: SafeUrl;
  public isLoaded: boolean;

  private tenantKey: string;
  private readonly subscriptions = new Array<Subscription>();

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly tokenStorage: TokenStorageService,
    private readonly cookieService: CookieService,
    private readonly configService: ConfigService
  ) {}

  public ngOnInit(): void {
    if (!this.tokenStorage.userInfo.CurrentSite.PowerSuitEidAuthEnabled) {
      this.tenantKey = JSON.parse(Base64.decode(this.tokens.IdToken.split('.')[1]))[
        this.configService.getConfig().powerDMSTenantKey
      ];
    } else {
      this.tenantKey = this.cookieService.get('dmssitekey');
    }

    this.subscribeFilePicker(this.tenantKey);
  }

  public ngAfterViewInit(): void {
    this.backdropRef?.nativeElement.showPopover();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((x) => x.unsubscribe());
  }

  public onLoad(): void {
    this.isLoaded = true;
    this.changeDetectorRef.detectChanges();
  }

  public subscribeFilePicker(siteKey: string): void {
    const config = this.configService.getConfig();
    const filePickerOrigin = config.powerDMSFilePickerOrigin;
    const powerdmsUrl = config.powerDMSBASEURL;
    const filePickerUrl = config.powerDMSFilePickerURL;

    let subscription = fromEvent<MessageEvent<FilePickerMessage>>(window, 'message')
      .pipe(filter((x) => x.origin === filePickerOrigin && x.data.type === 'DMS_FILEPICKER_FILESELECTED'))
      .pipe(
        map((e) => {
          if (this.useStructuredCallback) {
            // Return structured data with revisionId from postMessage payload
            return e.data.data.selectedFiles.map(
              (x) =>
                ({
                  documentId: String(x.documentId),
                  documentName: x.documentName,
                  documentUrl: `${powerdmsUrl}/link/${siteKey}/document/?id=${x.documentId}`,
                  revisionId: x.revisionId ? String(x.revisionId) : null
                } as PowerDmsFileInfo)
            );
          } else {
            // Legacy: return HTML anchor strings (revisionId not included in URL)
            return e.data.data.selectedFiles.map(
              (x) => `<a href="${powerdmsUrl}/link/${siteKey}/document/?id=${x.documentId}">${x.documentName}</a>`
            );
          }
        })
      )
      .subscribe((x) => {
        this.onSelectionCallback(x as any);
        this.onClosedCallback();
      });

    this.subscriptions.push(subscription);

    subscription = fromEvent<MessageEvent<FilePickerMessage>>(window, 'message')
      .pipe(filter((x) => x.origin === filePickerOrigin && x.data.type === 'DMS_FILEPICKER_CLOSE'))
      .subscribe(() => this.onClosedCallback());

    this.subscriptions.push(subscription);

    const tokenRefreshUrl = `${config.apiUrl}/power-dms/refresh`;

    const url = `${filePickerUrl}?access_token=${this.tokens.AccessToken}&id_token=${this.tokens.IdToken}&refresh_url=${tokenRefreshUrl}`;

    this.url = encodeURI(url);
  }
}

interface FilePickerMessage {
  type: string;
  data: SelectionResponse;
}

interface SelectionResponse {
  selectedFiles: ResponseFileInfo[];
}

interface ResponseFileInfo {
  documentId: string;
  documentName: string;
  breadcrumbs: ResponseParentFolder[];
  revisionId: string;
  revisionStatus: ObjectStatus;
  contentUrl: string;
}

enum ObjectStatus {
  Draft = 'draft',
  Published = 'published',
  Archived = 'archived'
}

interface ResponseParentFolder {
  id: number;
  name: string;
}
